import { Pitch, PrerenderedPhraseData } from 'Types';
import { midiPitchToFrequency } from 'Utils';
import {
  getMusicXmlKeyContextsByMeasure,
  getParallelModeConversionPitchDelta,
  type ParallelModeConversion,
} from './MusicXmlModeConversion';

const MIN_MIDI_NOTE = 0;
const MAX_MIDI_NOTE = 127;

type PitchClassMapping = {
  accidental: number;
  fundamentalNote: number;
};

const SHARP_PITCH_CLASS_MAP: Record<number, PitchClassMapping> = {
  0: { accidental: 2, fundamentalNote: 0 }, // C
  1: { accidental: 0, fundamentalNote: 0 }, // C#
  2: { accidental: 2, fundamentalNote: 2 }, // D
  3: { accidental: 0, fundamentalNote: 2 }, // D#
  4: { accidental: 2, fundamentalNote: 4 }, // E
  5: { accidental: 2, fundamentalNote: 5 }, // F
  6: { accidental: 0, fundamentalNote: 5 }, // F#
  7: { accidental: 2, fundamentalNote: 7 }, // G
  8: { accidental: 0, fundamentalNote: 7 }, // G#
  9: { accidental: 2, fundamentalNote: 9 }, // A
  10: { accidental: 0, fundamentalNote: 9 }, // A#
  11: { accidental: 2, fundamentalNote: 11 }, // B
};

const clampMidiNote = (midiNote: number): number =>
  Math.max(MIN_MIDI_NOTE, Math.min(MAX_MIDI_NOTE, midiNote));

export const transposePitchBySemitones = (pitch: Pitch, semitoneSteps: number): Pitch => {
  if (semitoneSteps === 0) {
    return pitch;
  }

  const sourceMidiNote = clampMidiNote(Math.round(pitch.halfTone + 12));
  const transposedMidiNote = clampMidiNote(sourceMidiNote + semitoneSteps);
  const pitchClass = ((transposedMidiNote % 12) + 12) % 12;
  const mapping = SHARP_PITCH_CLASS_MAP[pitchClass];

  return {
    accidental: mapping.accidental,
    fundamentalNote: mapping.fundamentalNote,
    octave: Math.floor(transposedMidiNote / 12) - 4,
    halfTone: transposedMidiNote - 12,
    frequency: midiPitchToFrequency(transposedMidiNote),
  };
};

export const transposeSerializedIteratorBySemitones = (
  serializedIterator: PrerenderedPhraseData[],
  semitoneSteps: number,
): PrerenderedPhraseData[] => {
  if (semitoneSteps === 0) {
    return serializedIterator;
  }

  return serializedIterator.map((phraseSlice) => ({
    ...phraseSlice,
    currentVoiceEntries: phraseSlice.currentVoiceEntries.map((voiceEntry) => ({
      ...voiceEntry,
      Notes: voiceEntry.Notes.map((note) => {
        if (note.isRest) {
          return note;
        }

        return {
          ...note,
          pitch: transposePitchBySemitones(note.pitch, semitoneSteps),
        };
      }),
    })),
  }));
};

export const transformSerializedIteratorPitches = (
  serializedIterator: PrerenderedPhraseData[],
  semitoneSteps: number,
  modeConversion: ParallelModeConversion = 'off',
  musicXml?: string,
): PrerenderedPhraseData[] => {
  const shouldApplyModeConversion = modeConversion !== 'off' && !!musicXml;
  if (semitoneSteps === 0 && !shouldApplyModeConversion) {
    return serializedIterator;
  }

  const keyContextsByMeasure =
    shouldApplyModeConversion && musicXml ? getMusicXmlKeyContextsByMeasure(musicXml) : undefined;

  return serializedIterator.map((phraseSlice) => {
    const keyContext = keyContextsByMeasure?.get(phraseSlice.currentMeasureIndex) ?? null;

    return {
      ...phraseSlice,
      currentVoiceEntries: phraseSlice.currentVoiceEntries.map((voiceEntry) => ({
        ...voiceEntry,
        Notes: voiceEntry.Notes.map((note) => {
          if (note.isRest) {
            return note;
          }

          const sourceMidiNote = Math.round(note.pitch.halfTone + 12);
          const modeDelta = getParallelModeConversionPitchDelta(
            sourceMidiNote,
            keyContext,
            modeConversion,
          );
          const totalSemitoneSteps = modeDelta + semitoneSteps;
          if (totalSemitoneSteps === 0) {
            return note;
          }

          return {
            ...note,
            pitch: transposePitchBySemitones(note.pitch, totalSemitoneSteps),
          };
        }),
      })),
    };
  });
};
